import { useState, useCallback } from "react";
import { policyAdminService } from "../api/policyAdmin/PolicyAdminService";
import {
  Application,
  ApplicationDocument,
} from "../api/policyAdmin/types/PolicyAdmin.types";

interface UsePolicyAdminState {
  application: Application | null;
  documents: ApplicationDocument[];
  loading: {
    application: boolean;
    documents: boolean;
  };
  error: {
    application: string | null;
    documents: string | null;
  };
}

interface UsePolicyAdminReturn extends UsePolicyAdminState {
  getApplication: (providerName: string, id: string) => Promise<{ isOk: boolean; isErr: boolean; value?: Application; error?: any }>;
  getDocuments: (
    providerName: string,
    id: string
  ) => Promise<{ isOk: boolean; isErr: boolean; value?: ApplicationDocument[]; error?: any }>;
  clearErrors: () => void;
  clearApplicationError: () => void;
  clearDocumentsError: () => void;
}

export const usePolicyAdmin = (): UsePolicyAdminReturn => {
  const [state, setState] = useState<UsePolicyAdminState>({
    application: null,
    documents: [],
    loading: {
      application: false,
      documents: false,
    },
    error: {
      application: null,
      documents: null,
    },
  });

  const getApplication = useCallback(
    async (providerName: string, id: string): Promise<{ isOk: boolean; isErr: boolean; value?: Application; error?: any }> => {
      setState((prev) => ({
        ...prev,
        loading: { ...prev.loading, application: true },
        error: { ...prev.error, application: null },
      }));

      try {
        const result = await policyAdminService.getApplicationById(providerName, id);

        if (result.isOk) {
          setState((prev) => ({
            ...prev,
            application: result.value,
            loading: { ...prev.loading, application: false },
          }));
          return result;
        } else {
          const errorMessage = result.error.message || "Failed to fetch application";
          setState((prev) => ({
            ...prev,
            application: null,
            loading: { ...prev.loading, application: false },
            error: { ...prev.error, application: errorMessage },
          }));
          return result;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error occurred";
        setState((prev) => ({
          ...prev,
          application: null,
          loading: { ...prev.loading, application: false },
          error: { ...prev.error, application: errorMessage },
        }));
        return { isOk: false, isErr: true, error };
      }
    },
    []
  );

  const getDocuments = useCallback(
    async (
      providerName: string,
      id: string
    ): Promise<{ isOk: boolean; isErr: boolean; value?: ApplicationDocument[]; error?: any }> => {
      setState((prev) => ({
        ...prev,
        loading: { ...prev.loading, documents: true },
        error: { ...prev.error, documents: null },
      }));

      try {
        const result = await policyAdminService.getDocuments(
          providerName,
          id
        );

        if (result.isOk) {
          setState((prev) => ({
            ...prev,
            documents: result.value || [],
            loading: { ...prev.loading, documents: false },
          }));
          return result;
        } else {
          const errorMessage = result.error.message || "Failed to fetch documents";
          setState((prev) => ({
            ...prev,
            documents: [],
            loading: { ...prev.loading, documents: false },
            error: { ...prev.error, documents: errorMessage },
          }));
          return result;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error occurred";
        setState((prev) => ({
          ...prev,
          documents: [],
          loading: { ...prev.loading, documents: false },
          error: { ...prev.error, documents: errorMessage },
        }));
        return { isOk: false, isErr: true, error };
      }
    },
    []
  );

  const clearErrors = useCallback(() => {
    setState((prev) => ({
      ...prev,
      error: { application: null, documents: null },
    }));
  }, []);

  const clearApplicationError = useCallback(() => {
    setState((prev) => ({
      ...prev,
      error: { ...prev.error, application: null },
    }));
  }, []);

  const clearDocumentsError = useCallback(() => {
    setState((prev) => ({
      ...prev,
      error: { ...prev.error, documents: null },
    }));
  }, []);

  return {
    ...state,
    getApplication,
    getDocuments,
    clearErrors,
    clearApplicationError,
    clearDocumentsError,
  };
};
